import { useEffect, useRef, useState } from 'react'

import { Box, Flex, useToast } from '@chakra-ui/react'
import DsrHeader from '../components/DsrHeader'
import DsrDynamicModal from '../messages/DsrDynamicModal'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import RenderDraftDsrContent from './RenderDraftDsrContent'
import _ from 'lodash'
import LaunchdeckButton from '../../global/components/helper-components/components/LaunchdeckButton';
import { triggerLaunchdeckToast } from '../../global/helpers/global-helpers';
import RenderPreviewDsrContent from './RenderPreviewDsrContent'
import useLaunchdeckModal from '../../global/hooks/use-launchdeck-modal'


export const DsrTemplateEditPage = ({
  dsrPagesRedux,
  showDsrModalRedux,
  userActionRequiredRedux,
  showDsrModalLoaderRedux,
  resetDsrTitle,
  resetDsrSelectedDisplaySubpage,
  resetDsrPages,
  unlockRoom,
  dsrInvalidRequestRedux,
  isMobileView,
  duplicateDsrPage,
  // Template
  fetchDsrTemplateData,
  dsrTemplateId,
  userAccountProfile,
  updateDsrTemplate,
  isLockedRoom,
  lockedRoomMetadata
}) => {
  const { templateId } = useParams();
  const history = useHistory();
  const toast = useToast();
  const location = useLocation();
  const { openLaunchdeckModal, closeLaunchdeckModal } = useLaunchdeckModal();

  const initRef = useRef(false);
  const lockedRoomRef = useRef(null);
  const timeoutRef = useRef(null);

  let [dsrPagesLocalState, setDsrPagesLocalState] = useState(null)
  let [dsrHasUnsavedChanges, setDsrHasUnsavedChanges] = useState(false)
  let [modalTransferPayload, setModalTransferPayload] = useState('')
  let [isPagePreviewMode, setIsPagePreviewMode] = useState(false)


  useEffect(() => {
    if (!initRef.current && templateId) {
      document.title = 'Launchdeck Digital Sales Room'
      document.description =
        'Create customised private digital sales rooms that let Sales & Purchasing teams agree on deals quickly'

        const queryDsrTemplateId = 'tmpl__' + templateId
        fetchDsrTemplateData(queryDsrTemplateId) // Different endpoint 
        
   
      initRef.current = true
    }
  }, [templateId, fetchDsrTemplateData])

  useEffect(() => {
    if (dsrInvalidRequestRedux === true) {
      history.push(`/invalid`, {
        redirectionUri: `/home`
      })
    }

  }, [dsrTemplateId, history, dsrInvalidRequestRedux])

  useEffect(() => {
    if (location.state) {
      const { toastMetadata } = location.state || {}

      if (toastMetadata) {
        toast(
          triggerLaunchdeckToast(toastMetadata)
        )
      }
    }
  }, [location.state]);


  useEffect(() => {
    dsrPagesRedux && setDsrPagesLocalState(_.cloneDeep(dsrPagesRedux))
  }, [dsrPagesRedux])


  useEffect(() => {
    if (dsrPagesLocalState) setDsrHasUnsavedChanges(!_.isEqual(dsrPagesLocalState, dsrPagesRedux))
  }, [dsrPagesRedux, dsrPagesLocalState, setDsrHasUnsavedChanges])


  useEffect(() => {
    if (dsrTemplateId && dsrHasUnsavedChanges) {

      updateDsrTemplate({
        dsrTemplateId,
        // Note: Future scope - save only the parts that have changed, and not all the pages. Less risk of wiping out error data.
        dsrPages: dsrPagesLocalState,
        sourceEvent: 'save-dsr-template'
      })

    }

  }, [dsrHasUnsavedChanges, dsrTemplateId])

  
  useEffect(() => {
    if (lockedRoomMetadata) {
      const { isLockerUser, noLockRequired } = lockedRoomMetadata || {}
      if (!!isLockerUser && noLockRequired !== true) {
        const handleBeforeUnload = event => {

          unlockRoom({
            dsrTemplateId,
            sourceEvent: 'unlock-room',
          });
        }

        window.addEventListener('beforeunload', handleBeforeUnload);

        // Proper cleanup function
        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      };
    }
  }, [lockedRoomMetadata]);

  useEffect(() => {
    if (lockedRoomMetadata) {
      const { isLockerUser, noLockRequired } = lockedRoomMetadata || {}
      if (!!isLockerUser && noLockRequired !== true) {
        const unlisten = history.listen(() => {
          unlockRoom({
            dsrTemplateId,
            sourceEvent: 'unlock-room',
          });
        });

        return () => {
          unlisten(); // Cleanup listener when component unmounts
        };
      };
    }
  }, [history, lockedRoomMetadata, dsrTemplateId]);

  // Set the timer
  const startUnlockTimer = () => {
    if (timeoutRef.current) { // ABC: Clear the previous timeout
      clearTimeout(timeoutRef.current); // ABC: Clear the previous timeout
    }

    timeoutRef.current = setTimeout(() => {
      unlockRoom({
        dsrTemplateId,
        sourceEvent: 'unlock-room',
      });
      handleIdleRoomUnlocked()
    }, 1000 * 60 * 60); // Timeout duration in milliseconds (1 hr)
  };

  const handleActivity = () => {
    // Reset the timer whenever an activity is detected
    startUnlockTimer();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      startUnlockTimer(); // Reset the timer when the user returns to the page
    }
  };


  useEffect(() => {
    if (lockedRoomMetadata) {
      const { noLockRequired } = lockedRoomMetadata || {};
      if (noLockRequired !== true) {
        // Start the initial timer
        startUnlockTimer();

        // Cleanup: Clear timeout when component is unmounted or dependencies change
        return () => {
          if (timeoutRef.current) { // ABC: Added check and clear timeout on unmount
            clearTimeout(timeoutRef.current); // ABC: Clear timeout to prevent memory leak
          }
        };
      }
    }

  }, [lockedRoomMetadata]);

  useEffect(() => {
    if (lockedRoomMetadata) {
    const { noLockRequired } = lockedRoomMetadata || {};
    if (noLockRequired !== true) {
      const activityEvents = ['load', 'mousedown', 'touchstart', 'click', 'keypress', 'mousemove', 'keydown'];

      // Add event listeners for user activity
      activityEvents.forEach(eventType => {
        document.addEventListener(eventType, handleActivity);
      });

      // Listen for visibility change events
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup function to remove event listeners and clear the timer when the component unmounts
      return () => {
        activityEvents.forEach(eventType => {
          document.removeEventListener(eventType, handleActivity);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);

        // Clear the timeout if the component unmounts
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }
  }, [dsrTemplateId, lockedRoomMetadata]);

  useEffect(() => {
    if (isLockedRoom === true && !lockedRoomRef.current) {
      setIsPagePreviewMode(true)

      lockedRoomRef.current = true
    }

  }, [setIsPagePreviewMode, isLockedRoom])

  const handleIdleRoomUnlocked = () => {
    openLaunchdeckModal('idle-room-unlocked')
  }

  const handleDsrModalClose = () => {
    closeLaunchdeckModal()
  }

  const handleChangeUserActionRequired = (newUserActionRequired, modalTransferPayload) => {
    setModalTransferPayload(modalTransferPayload)
    openLaunchdeckModal(newUserActionRequired)
  }

  const handlerSetDsrPages = (pages) => {
    setDsrPagesLocalState(pages)
  }

  const handlerAddDsrPage = (newDsrPage, sourcePageId) => {
    if (!sourcePageId) {
      // Adds brand new page
      setDsrPagesLocalState((prevState) => [...prevState, newDsrPage])
    } else {
      // Puts duplicated page right below source page
      const sourcePageIndex = dsrPagesLocalState?.findIndex((page) => page.pageId === sourcePageId)
      if (sourcePageIndex >=0) {
        const newPagesArray = [
          ...dsrPagesLocalState.slice(0, sourcePageIndex + 1),
          newDsrPage, // Insert the new object
          ...dsrPagesLocalState.slice(sourcePageIndex + 1), 
        ];

        setDsrPagesLocalState(newPagesArray)
      }
    }
  }

  const handlerRemoveDsrPage = (dsrPageId) => {
    setDsrPagesLocalState((prevState) => prevState.filter((page) => page.pageId !== dsrPageId))
  }

  useEffect(() => {
    return () => resetDsrTitle() && resetDsrSelectedDisplaySubpage() && resetDsrPages()
  }, [resetDsrTitle, resetDsrSelectedDisplaySubpage, resetDsrPages])


  if (isMobileView) {
    return (
      <Flex margin='-2% -7% -7% -7%' height='100vh' justifyContent='center' alignItems='center' background='white' flexDirection='column'>
        <Flex mb='0.625em' px='2em' justify='center' textAlign='center'>
        For the best experience editing your content, we recommend using a computer's wider display
        </Flex>
        <Flex direction='column' mt='1em' justify='center' alignItems='center'>
          <Flex justify={['center', 'flex-end', 'flex-end', 'flex-end']} w='100%'>
            <LaunchdeckButton bg='brand.500' color='white' onClick={() => window.location.replace('/home')} label='Edit on a computer later' />
          </Flex>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex direction="column" height='100%'>
      <Flex direction="row" flexGrow="1">
        <Flex w="100%" direction="column" alignItems="center" bg="white" rounded="0.8em">
          <Box width='100%' position='fixed' zIndex='2'>
            <DsrHeader
              dsrTemplateId={dsrTemplateId}
              dsrPages={dsrPagesLocalState} 
              isEditMode={true}
              orgLogo={userAccountProfile?.orgLogo}
              handleChangeUserActionRequired={handleChangeUserActionRequired}
            />
          </Box>

          <Flex w="100%" className="authentication-container" justify="center" marginTop='140px'>
            { isPagePreviewMode
              ? (
                <RenderPreviewDsrContent
                  pagesLocalState={dsrPagesLocalState}
                />
              )
              : 
                <RenderDraftDsrContent
                  pagesLocalState={dsrPagesLocalState}
                  replaceDsrPages={handlerSetDsrPages}
                  addDsrPage={handlerAddDsrPage}
                  removeDsrPage={handlerRemoveDsrPage}
                  duplicateDsrPage={duplicateDsrPage}
                  handleChangeUserActionRequired={handleChangeUserActionRequired}
                />
            }
            
            <DsrDynamicModal
              dsrTemplateId={dsrTemplateId}
              userActionRequired={userActionRequiredRedux}
              showDsrModal={showDsrModalRedux}
              showDsrModalLoader={showDsrModalLoaderRedux}
              handleDsrModalClose={handleDsrModalClose}
              handleChangeUserActionRequired={handleChangeUserActionRequired}
              modalTransferPayload={modalTransferPayload}
            />

          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}
